import React from "react";

const PrivacyPolicy = () => {
  return (
    <div>
      <h1>Privacy Policy</h1>
      <p>Last Updated: 11th August 2026</p>

      <p>
        This Privacy Policy explains how Paul Frederick David Walsh ("Owner",
        "Operator", "we", "us" or "our") collects, uses, stores and shares
        personal information in connection with the Pogforts services. The
        Services include pogforts.uk, the Pogforts web application and Discord
        server, the Magical Sorting Bat Discord bot ("MSB"), Pogforts: Murder
        Mystery Magic, authenticated account features, game systems, community
        functions and related services that we operate from time to time.
      </p>

      <p>
        For personal information processed for the purposes described in this
        policy, Paul Frederick David Walsh is the data controller unless a specific
        feature identifies another controller. This policy is intended to provide
        the privacy information required under applicable UK data protection law,
        including the UK GDPR, the Data Protection Act 2018 and relevant amendments
        made by the Data (Use and Access) Act 2025.
      </p>

      <h2>1. Scope and Definitions</h2>
      <ul>
        <li>
          <strong>Personal Information or Personal Data:</strong> information that
          identifies or relates to an identifiable living person.
        </li>
        <li>
          <strong>Account Data:</strong> information associated with an account or
          identity used to access the Services, including a Discord account where
          applicable.
        </li>
        <li>
          <strong>Usage Data:</strong> technical, security and activity information
          generated when the Services are accessed or used.
        </li>
        <li>
          <strong>Service Data:</strong> game, profile, community, transaction and
          operational records generated through use of the Services.
        </li>
        <li>
          <strong>You:</strong> the individual using or interacting with the
          Services, or a parent, guardian or authorised representative acting for
          another individual where appropriate.
        </li>
      </ul>

      <h2>2. Information We Collect</h2>
      <p>Depending on the features you use, we may process the following:</p>

      <h3>Discord and Account Information</h3>
      <ul>
        <li>Discord user ID;</li>
        <li>Discord username and display or global name;</li>
        <li>Discord discriminator where available;</li>
        <li>Discord avatar information;</li>
        <li>authentication and session identifiers;</li>
        <li>account creation, session creation and expiry information.</li>
      </ul>

      <h3>Game and Profile Information</h3>
      <ul>
        <li>
          profile and game statistics such as Pogs, vault balance, XP, house,
          merits, strikes, wins, losses, kills, deaths, heals and similar game
          attributes;
        </li>
        <li>
          game inventory and progression information such as wands, spells,
          charms, weaponry, protection, hardware, gadgets, ultimates, status
          effects and related timestamps;
        </li>
        <li>
          roulette and other game records, including spin identifiers, bets,
          stakes, selections, results, payouts, outcomes and timestamps;
        </li>
        <li>
          house, leaderboard, message-count or other community/game statistics
          generated by participation in Pogforts.
        </li>
      </ul>

      <h3>Website, Analytics and Security Information</h3>
      <ul>
        <li>pages or routes accessed and timestamps;</li>
        <li>
          browser or user-agent information supplied by your device or browser;
        </li>
        <li>
          event information used for security and operational monitoring, which
          may include page views, blocked developer-tool shortcuts, right-click
          attempts and similar security signals;
        </li>
        <li>
          server, request, diagnostic and security information generated by the
          hosting and application environment, which may include network or device
          information where technically recorded by those systems.
        </li>
      </ul>

      <h3>Bookings, Contact and Payment-Related Information</h3>
      <p>
        Where a booking, educational service or payment feature is used, we may
        process information such as:
      </p>
      <ul>
        <li>student or customer name;</li>
        <li>email address and mobile number;</li>
        <li>age group;</li>
        <li>selected hours, subject, delivery method and package information;</li>
        <li>order identifier, amount and payment status;</li>
        <li>payment method or payment-type information returned by the processor;</li>
        <li>
          operational payment notifications and status history needed to administer
          the booking or transaction.
        </li>
      </ul>
      <p>
        Full payment-card or bank credentials are intended to be handled by the
        relevant payment processor rather than stored directly in the Pogforts
        application where the payment flow is provided by that processor.
      </p>

      <h3>Content, Reports and Communications</h3>
      <ul>
        <li>
          information you submit through forms, administrative tools, reports,
          moderation channels or support communications;
        </li>
        <li>
          blog or content-publishing information where you are authorised to use
          publishing features;
        </li>
        <li>
          records reasonably required to investigate abuse, disputes, security
          incidents or policy breaches.
        </li>
      </ul>

      <h2>3. How We Obtain Information</h2>
      <p>We may obtain personal information:</p>
      <ul>
        <li>directly from you when you submit or provide it;</li>
        <li>
          from Discord when you use Discord authentication or interact with MSB or
          Pogforts Discord features;
        </li>
        <li>
          automatically when you access or use the website, application, game or
          bot;
        </li>
        <li>
          from payment processors or other service providers where necessary to
          complete, verify or administer a transaction;
        </li>
        <li>
          from moderators, administrators or other users when they submit a report
          or information relevant to community safety or enforcement.
        </li>
      </ul>

      <h2>4. Purposes and Lawful Bases</h2>
      <p>
        We only process personal information where we have a lawful basis. The
        basis depends on the information and purpose involved.
      </p>
      <ul>
        <li>
          <strong>Contract or steps at your request:</strong> where processing is
          necessary to provide an account, authenticated feature, game function,
          booking, transaction or other Service you request.
        </li>
        <li>
          <strong>Legitimate interests:</strong> where reasonably necessary to
          operate, secure, improve and administer the Services; prevent abuse and
          fraud; maintain game integrity; keep audit records; understand usage;
          moderate communities; resolve disputes; or protect users and systems,
          provided those interests are not overridden by your rights and interests.
        </li>
        <li>
          <strong>Legal obligation:</strong> where processing is necessary to
          comply with applicable law, lawful requests, accounting, tax, consumer,
          regulatory or data-protection obligations.
        </li>
        <li>
          <strong>Consent:</strong> where the law requires consent or where we
          choose to rely on consent for a particular optional activity. You may
          withdraw consent at any time, although withdrawal does not affect the
          lawfulness of processing carried out before withdrawal.
        </li>
      </ul>

      <h2>5. How We Use Personal Information</h2>
      <p>We may use personal information to:</p>
      <ul>
        <li>authenticate users and maintain sessions;</li>
        <li>provide account, profile, game, bot and community functionality;</li>
        <li>calculate and maintain game balances, rankings, outcomes and records;</li>
        <li>process and administer bookings and transactions;</li>
        <li>respond to support, privacy, moderation and security requests;</li>
        <li>detect abuse, cheating, unauthorised access and security threats;</li>
        <li>monitor reliability, diagnose faults and improve the Services;</li>
        <li>maintain audit trails and investigate disputes or incidents;</li>
        <li>comply with legal and regulatory obligations;</li>
        <li>
          establish, exercise or defend legal claims where reasonably necessary.
        </li>
      </ul>

      <h2>6. Cookies, Authentication and Similar Technologies</h2>
      <p>
        Pogforts uses cookies or equivalent browser storage where necessary to
        support authentication and security. Current website authentication may
        use a time-limited session cookie and short-lived OAuth state or return
        cookies during Discord login. These controls are used to maintain a signed-
        in session, complete the login flow and reduce authentication abuse.
      </p>
      <p>
        Some cookies that are strictly necessary for a service requested by you may
        be used without optional consent where permitted by law. If non-essential
        cookies or similar technologies requiring consent are introduced, we will
        provide appropriate information and obtain consent where required by the
        Privacy and Electronic Communications Regulations and applicable data
        protection law.
      </p>

      <h2>7. Sharing and Disclosure</h2>
      <p>
        We do not sell personal information. We may disclose limited personal
        information where reasonably necessary to the following recipients:
      </p>
      <ul>
        <li>
          <strong>Discord:</strong> where Discord authentication, bot functionality,
          community administration, user-resolution features or operational
          notifications depend on Discord services;
        </li>
        <li>
          <strong>Payment processors:</strong> where needed to create, verify,
          process or administer a payment or booking;
        </li>
        <li>
          <strong>Hosting, database, infrastructure and technical providers:</strong>
          where they process information to host, secure, transmit, store or
          support the Services on our behalf;
        </li>
        <li>
          <strong>Authorised moderators or administrators:</strong> where access is
          reasonably necessary for moderation, support, security, payment
          administration or operational duties;
        </li>
        <li>
          <strong>Professional advisers, authorities or courts:</strong> where
          disclosure is necessary to obtain advice, comply with law, respond to a
          lawful request, protect rights or address legal proceedings.
        </li>
      </ul>
      <p>
        Where payment-related operational alerts are configured, limited order
        information such as an order identifier, student or customer name, payment
        status, payment type and amount may be transmitted to a designated Discord
        administration channel for operational handling.
      </p>

      <h2>8. International Data Transfers</h2>
      <p>
        Some third-party services used by Pogforts may process information outside
        the United Kingdom. Where UK data protection law restricts an international
        transfer, we will rely on an applicable lawful transfer mechanism or other
        protection required by law. Third-party providers may also act as separate
        controllers for information they process under their own terms.
      </p>

      <h2>9. Data Retention</h2>
      <p>
        We retain personal information only for as long as reasonably necessary for
        the purpose for which it was collected, including to operate the Services,
        maintain game integrity, administer transactions, investigate incidents,
        resolve disputes, enforce agreements and comply with legal obligations.
      </p>
      <ul>
        <li>
          OAuth state and return information used during login is intended to be
          short-lived.
        </li>
        <li>
          Authentication sessions are time-limited and may expire or be removed on
          logout, subject to technical and security records that may remain for a
          separate lawful purpose.
        </li>
        <li>
          Account and game records may be retained while an account or game profile
          remains active and for a reasonable period afterwards where required for
          integrity, audit, dispute or legal purposes.
        </li>
        <li>
          Transaction and booking information may be retained for accounting,
          payment, dispute, consumer-protection and legal-record purposes.
        </li>
        <li>
          Analytics and security records may be retained or archived for as long as
          reasonably necessary to investigate security, abuse, reliability and
          operational trends.
        </li>
      </ul>
      <p>
        When information is no longer required, we will delete, anonymise or
        otherwise put it beyond ordinary use where reasonably practicable, subject
        to backups, legal holds and other lawful retention requirements.
      </p>

      <h2>10. Security</h2>
      <p>
        We use technical and organisational measures intended to protect personal
        information against unauthorised access, alteration, disclosure, loss or
        destruction. Measures may include access controls, authentication,
        security monitoring, restricted administrative access and server-side
        validation. No internet or storage system can be guaranteed completely
        secure.
      </p>
      <p>
        Where a personal data breach creates a legal obligation to notify affected
        individuals or the Information Commissioner's Office, we will make the
        required notification in accordance with applicable law.
      </p>

      <h2>11. Children and Young People</h2>
      <p>
        Some Pogforts or associated educational features may concern children or
        young people. Where a booking or service relates to a child, a parent,
        guardian or other responsible adult should provide contact, booking and
        payment information on the child's behalf where required.
      </p>
      <p>
        We aim to use children's personal information only where necessary for the
        relevant service and with appropriate regard to age, transparency,
        minimisation, security and the child's interests. Where consent is relied
        on for an information-society service offered directly to a child, the age
        and parental-authorisation rules required by UK law will be applied. If we
        become aware that children's information has been collected in a manner
        that is not lawful or necessary, we will take appropriate corrective
        action.
      </p>

      <h2>12. Automated Game Mechanics</h2>
      <p>
        Pogforts uses automated rules and randomisation for game functions,
        including some chance-based outcomes. These game mechanics are not intended
        to make decisions about you that produce legal or similarly significant
        effects within the meaning of data protection law. If we introduce such
        automated decision-making, we will provide any additional information and
        safeguards required by law.
      </p>

      <h2>13. Your Data Protection Rights</h2>
      <p>
        Depending on the circumstances and lawful basis, you may have rights to:
      </p>
      <ul>
        <li>be informed about how your personal information is used;</li>
        <li>request access to your personal information;</li>
        <li>request correction of inaccurate or incomplete information;</li>
        <li>request erasure in circumstances where the right applies;</li>
        <li>request restriction of processing in circumstances where it applies;</li>
        <li>receive or transfer certain information under the right to portability;</li>
        <li>withdraw consent where processing is based on consent;</li>
        <li>
          object to processing in circumstances where the right to object applies;
        </li>
        <li>
          complain about how your personal information has been handled.
        </li>
      </ul>
      <p>
        These rights are not absolute and exemptions may apply. We may need to
        verify your identity before acting on a request.
      </p>

      <h2>14. Your Right to Object</h2>
      <p>
        Where we rely on legitimate interests, you may have the right to object to
        processing based on your particular situation. If personal information is
        ever processed for direct marketing, you have the right to object to that
        direct marketing. We will consider and respond to objections in accordance
        with applicable data protection law.
      </p>

      <h2>15. Data Protection Complaints</h2>
      <p>
        If you believe we have mishandled your personal information, you can raise a
        data protection complaint with us through the Pogforts Discord server or
        another privacy contact method we publish. Please provide enough detail for
        us to identify the issue and the information concerned.
      </p>
      <p>
        We will handle data protection complaints in accordance with applicable UK
        law, including acknowledging qualifying complaints within the legally
        required period and responding without undue delay. You may also complain
        to the Information Commissioner's Office if you are dissatisfied with our
        handling of your personal information.
      </p>
      <p>
        Information Commissioner's Office: {" "}
        <a
          href="https://ico.org.uk/make-a-complaint/"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://ico.org.uk/make-a-complaint/
        </a>
      </p>

      <h2>16. Third-Party Links and Services</h2>
      <p>
        The Services may link to or integrate with third-party services that have
        their own privacy practices. We do not control the independent processing
        carried out by those third parties acting as controllers. You should review
        their privacy information before providing personal information to them.
      </p>

      <h2>17. Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy to reflect changes in law, technology,
        Service functionality or our processing activities. The current version
        will be published on pogforts.uk. Where required, we will provide additional
        notice or obtain fresh consent before materially changing a consent-based
        processing activity.
      </p>

      <h2>18. Governing Framework</h2>
      <p>
        This Privacy Policy is governed by applicable UK data protection and privacy
        law. Nothing in this policy limits any statutory privacy right or remedy
        available to you.
      </p>

      <h2>19. Contact Information</h2>
      <p>
        Privacy requests, questions or complaints may be raised through the
        Pogforts Discord server: {" "}
        <a
          href="https://discord.gg/8WF9QQJsVz"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://discord.gg/8WF9QQJsVz
        </a>
      </p>
    </div>
  );
};

export default PrivacyPolicy;
